import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useVault } from "@/contexts/VaultContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Wraps every Finance & Payroll route. If the vault is unlocked, renders
 * children. Otherwise renders an inline unlock card with a password
 * input + a Back-to-clients link.
 */
export default function VaultedRoute({ children }) {
  const vault = useVault();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reveal, setReveal] = useState(false);

  // When the vault becomes unlocked, focus the page content.
  useEffect(() => {
    if (vault.unlocked) setPassword("");
  }, [vault.unlocked]);

  if (vault.loading) {
    return (
      <div className="p-12 text-center text-zinc-500 text-sm">
        <Lock className="h-5 w-5 inline animate-pulse" /> Checking vault…
      </div>
    );
  }
  if (vault.unlocked) return children;

  const submit = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setSubmitting(true);
    const result = await vault.unlock(password);
    setSubmitting(false);
    if (result.ok) {
      toast.success("Vault unlocked");
    } else {
      toast.error(result.detail);
      setPassword("");
    }
  };

  return (
    <div className="p-6 max-w-md mx-auto" data-testid="vault-unlock-card">
      <Card className="rounded-sm border-zinc-200 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-sm bg-amber-100">
              <Lock className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <div className="label-overline">SECOND-FACTOR</div>
              <div className="font-display font-bold text-lg">
                Legal · Finance · HR vault
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed">
            This section is protected by a separate vault password. Enter
            it once per browser session (8 hours) to access Xero, KeyPay,
            legal documents and other finance data.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500">
              Vault password
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type={reveal ? "text" : "password"}
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  className="h-10 font-mono text-sm"
                  data-testid="vault-unlock-password" />
                <button type="button" onClick={() => setReveal((r) => !r)}
                  className="h-10 px-2 border border-zinc-200 rounded-sm hover:bg-zinc-50"
                  data-testid="vault-unlock-toggle-reveal">
                  {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
            <div className="flex items-center gap-2">
              <Button type="submit" className="rounded-sm flex-1"
                disabled={!password || submitting}
                data-testid="vault-unlock-submit">
                {submitting
                  ? <span className="inline-flex items-center gap-2"><Lock className="h-3.5 w-3.5 animate-pulse" /> Verifying…</span>
                  : <span className="inline-flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Unlock</span>}
              </Button>
              <Button type="button" variant="ghost" className="rounded-sm"
                onClick={() => navigate("/clients", { replace: true,
                  state: { from: location.pathname } })}
                data-testid="vault-unlock-cancel">
                Cancel
              </Button>
            </div>
          </form>
          <div className="text-[10px] text-zinc-400 leading-relaxed">
            Forgotten the vault password? Ask the Administrator — only
            they can change it (Admin Team → Vault settings).
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
